import { FetchAPI, ParamsSerializer } from './auth-service';
import _get from 'lodash/get';

const fields = [
    {
        variable: 'detector_id',
        hide: true,
        headerName: 'ID',
        field: 'detector_id',
        type: 'string',
        sortable: true,
        width: 150,
        valueGetter: p => _get(p, 'row._id') || _get(p, 'row._id', ''),
    },
    {
        variable: 'name',
        hidden: false,
        headerName: 'Name',
        field: 'name',
        type: 'string',
        sortable: true,
        width: 200,
        valueGetter: p => _get(p, 'row._source.name', '') || _get(p, 'row.name', ''),
    },
    {
        variable: 'description',
        hidden: false,
        headerName: 'Description',
        field: 'description',
        type: 'string',
        sortable: true,
        flex: 1,
        minWidth: 150,
        valueGetter: p => _get(p, 'row._source.description') || _get(p, 'row.description', ''),
    },
    {
        variable: 'index',
        hidden: false,
        headerName: 'Index',
        field: 'index',
        type: 'string',
        sortable: true,
        width: 200,
        valueGetter: p => _get(p, 'row._index', '') || _get(p, 'row.indices', ''),
    },
    {
        variable: 'filters',
        hidden: false,
        headerName: 'Has Filters',
        field: 'filters',
        type: 'string',
        sortable: false,
        width: 100,
        align: 'center',
        valueGetter: p => '',
    },
    {
        variable: 'enabled',
        hidden: false,
        headerName: 'Activate All',
        field: 'enabled',
        type: 'actions',
        sortable: false,
        width: 150,
        valueGetter: p => _get(p.row.enabled, false),
    },
    {
        variable: 'edit',
        hidden: false,
        headerName: 'Edit',
        field: 'edit',
        type: 'actions',
        sortable: false,
        width: 50,
    }
];

const filterTypes = [
    {
        value: 'Grade',
        min: 0,
        max: 1.0001,
        step: 0.1
    },
    {
        value: 'Confidence',
        min: 0,
        max: 5.0001,
        step: 0.1
    }
];

const filterOperators = [
    {
        value: '=',
        label: 'Equal To (=)',
    },
    {
        value: '!=',
        label: 'Not Equal To (!=)',
    },
    {
        value: '>',
        label: 'Greater Than (>)',
    },
    {
        value: '>=',
        label: 'Greater Than or Eq To (>=)',
    },
    {
        value: '<',
        label: 'Less Than (<)',
    },
    {
        value: '<=',
        label: 'Less Than or Eq To (<=)',
    },
];

const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

class OSAnomalyRulesDataService {
    constructor () {
        if (!OSAnomalyRulesDataService.instance) {
            OSAnomalyRulesDataService.instance = this;
        }
        this.searchPlaceholder = 'Search rules';
        this.fields = fields;
        this.filterTypes = filterTypes;
        this.filterOperators = filterOperators;

        this.listRules = (search, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            const params = {
                q: search || '',
                limit: 100,
            };

            return FetchAPI({
                method: 'GET',
                url: `${prefix}anomdetect/management/adrules?distilled&${ParamsSerializer(params)}`
                // url: `${prefix}anomdetect/management/adrules?${ParamsSerializer(params)}`
            }).then(res => {
                return res;
            }, e => {
                console.error('Unable to get anomaly detection rules : ', e);
            });
        };

        this.listStockRules = (search, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            const params = {
                q: search || '',
            };

            return FetchAPI({
                method: 'GET',
                url: `${prefix}anomdetect/management/adstockrules`
            }).then(res => {
                return res;
            }, err => {
                console.error('Unable to get stock anomaly detection rules : ', err);
            });
        };

        this.createDetector = (id, desc) => {
            return FetchAPI({
                method: 'POST',
                url: 'anomdetect/management/adrules',
                body: {
                    anomalyRuleIdentifer: id,
                    description: desc,
                }
            });
        };

        this.getDetector = (id, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            return FetchAPI({
                method: 'GET',
                url: `${prefix}anomdetect/management/adrules/${id}`
            });
        };

        this.enableAnomalyRule = (id, enabled, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            return FetchAPI({
                method: 'POST',
                url: `${prefix}anomdetect/management/adrules/${id}/${enabled ? '_start' : '_stop'}`,
                body: {}
            });
        };

        this.listFilters = (cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            return FetchAPI({
                method: 'GET',
                url: `${prefix}anomdetect/filters`
            });
        };

        this.listRuleFilters = (name, id, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            return FetchAPI({
                method: 'GET',
                url: `${prefix}anomdetect/filters/${id}/${name}`
            });
        };

        this.updateRuleFilters = (name, id, filters, cmc) => {
            if (MC2CHECK && !cmc) return Promise.resolve('no cmc');
            const prefix = MC2CHECK && cmc ? `mc2/cmcapi/${cmc}/` : '';

            return FetchAPI({
                method: 'POST',
                url: `${prefix}anomdetect/filters/${name}/${id}`,
                body: filters
            });
        };

        this.getSavedSearches = () => {
            try {
                let searches = localStorage.getItem('savedSearches_osAnlomalyRules') || '[]';
                if (searches.length < 1) searches = '[]';
                return JSON.parse(searches);
            } catch (err) {
                console.log('getSavedSearches error', err);
                return [];
            }
        };

        this.saveSearch = (str) => {
            if (!str || !str.length) return;
            const searches = this.getSavedSearches();
            const i = searches.findIndex(s => s === str);
            if (i >= 0) searches.splice(i, 1);
            searches.unshift(str);
            searches.splice(5);
            localStorage.setItem('savedSearches_osAnlomalyRules', JSON.stringify(searches));
        };

        return OSAnomalyRulesDataService.instance;
    }
}
const OSAnomalyRulesService = new OSAnomalyRulesDataService();

export {
    OSAnomalyRulesService as default,
    fields
};
